import { SilentRequest } from '@azure/msal-browser';

import { msalInstance } from '@/main';

// Token cache to avoid unnecessary requests - reduced cache time for security
let cachedToken: { token: string; expiresAt: number; roles?: string[] } | null =
	null;

// Maximum cache time: 15 minutes (instead of using full token lifetime)
const MAX_CACHE_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds

export function getAccessTokenRequest(): SilentRequest {
	const msalScopes = import.meta.env.VITE_MSAL_SCOPES;

	const accessTokenRequest: SilentRequest = {
		scopes: [msalScopes],
		account: msalInstance.getAllAccounts()[0],
	};

	return accessTokenRequest;
}

export const validateUserAccess = (): boolean => {
	const account = msalInstance.getActiveAccount();
	if (!account) return false;

	const requiredScope = import.meta.env.VITE_REQUIRED_SCOPE;
	const userRoles = account?.idTokenClaims?.roles || [];
	// Check if user still has required scope/role
	if (requiredScope && !userRoles.includes(requiredScope)) {
		console.warn('User no longer has required access scope');
		return false;
	}

	return true;
};

export const getUserToken = async (): Promise<string> => {
	try {
		// Always validate user access first
		if (!validateUserAccess()) {
			clearTokenCache();
			throw new Error('User access validation failed');
		}

		// Check if we have a valid cached token (with shorter cache time)
		if (cachedToken && cachedToken.expiresAt > Date.now()) {
			return cachedToken.token;
		}

		const response = await msalInstance.acquireTokenSilent(
			getAccessTokenRequest(),
		);

		// Validate the fresh token
		const account = msalInstance.getActiveAccount();
		const currentRoles = account?.idTokenClaims?.roles || [];

		// Use shorter cache time for better security (max 15 minutes or token expiry, whichever is shorter)
		const tokenExpiry = response.expiresOn?.getTime() || Date.now() + 3600000;
		const maxCacheExpiry = Date.now() + MAX_CACHE_TIME;
		const expiresAt = Math.min(tokenExpiry - 300000, maxCacheExpiry); // 5 min safety buffer

		cachedToken = {
			token: response.accessToken,
			expiresAt,
			roles: currentRoles,
		};

		return response.accessToken;
	} catch (error) {
		// Clear cached token on error
		cachedToken = null;
		throw error;
	}
};

export const getUserTokenToApiRequest = async (config: any) => {
	try {
		const userToken = await getUserToken();
		return {
			...config,
			headers: {
				...config.headers,
				Authorization: `Bearer ${userToken}`,
			},
		};
	} catch (error) {
		// If token acquisition fails, clear cache and rethrow
		cachedToken = null;
		throw error;
	}
};

export const clearTokenCache = () => {
	cachedToken = null;
};

export const forceTokenRefresh = async (): Promise<string> => {
	clearTokenCache();
	return getUserToken();
};
